import { NgClass, AsyncPipe } from '@angular/common';
import {
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
    Renderer2,
    Type,
    ViewContainerRef,
    ViewEncapsulation,
    viewChild,
    inject,
    DOCUMENT,
    CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ApContentService, ApContentPipe } from '@ap/content';
import '@asn/icon';
import { ApDarkmodeService } from '@ap/darkmode';

import {
    AP_MODAL_HEADER_EXTERNAL_CONTROL,
    ModalClosedReason,
    ModalClosedResult,
    ModalContainer,
    ModalContent,
    ModalOptions,
    ModalSize
} from './modal.interface';
import content from './modal.content.json';

let modalNumber = 0;

/**
 * @ngdoc component
 * @name ApModalComponent
 */
@Component({
    selector: 'ap-modal',
    templateUrl: './modal.component.html',
    styleUrls: ['./modal.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [NgClass, AsyncPipe, ApContentPipe],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ApModalComponent implements ModalContainer, OnInit, OnDestroy {
    private readonly contentService = inject(ApContentService);
    private readonly elementRef = inject(ElementRef);
    private readonly document = inject(DOCUMENT, { optional: true });
    private readonly renderer = inject(Renderer2);
    private readonly router = inject(Router, { optional: true });
    externalHeader = inject(AP_MODAL_HEADER_EXTERNAL_CONTROL);
    private readonly darkmodeService = inject(ApDarkmodeService);

    public readonly componentContainer = viewChild('componentContainer', { read: ViewContainerRef });

    public readonly componentFooter = viewChild('componentFooter', { read: ViewContainerRef });

    public modalOptions: ModalOptions<any> = new ModalOptions();
    public zIndex: number;
    public currentModalNumber: number;
    public isMouseDownInsideModal = false;
    public size: ModalSize = ModalSize.MEDIUM;

    public darkmodeSetting$: 'light' | 'dark' = this.darkmodeService.getDarkmodeSetting();

    private readonly subject: Subject<any> = new Subject<ModalClosedResult>();
    private readonly subscriptions: Subscription = new Subscription();

    private themeHandler = (mode: 'light' | 'dark') => {
        this.darkmodeSetting$ = mode;
    };

    @HostListener('window:popstate')
    public onPopState(): void {
        this.dispose();
    }

    public onMouseDown(event: MouseEvent) {
        this.isMouseDownInsideModal = this.isMouseEventInModal(event);
    }

    public onMouseUp(event: MouseEvent) {
        const isMouseUpInsideModal = this.isMouseEventInModal(event);

        const modalContainer = this.elementRef.nativeElement.querySelector('.ap-modal') as HTMLElement;
        const parentModal = modalContainer?.parentElement?.hidden;

        if (this.currentModalNumber === modalNumber && !this.isMouseDownInsideModal && !isMouseUpInsideModal && !parentModal) {
            this.close(ModalClosedReason.CANCELLED);
        }
    }

    public ngOnInit(): void {
        modalNumber++;
        this.currentModalNumber = modalNumber;
        this.size = this.modalOptions?.size ? this.modalOptions.size : ModalSize.MEDIUM;

        this.darkmodeService.subscribe(this.themeHandler);
        this.contentService.addContentItems(content);
        if (this.router) {
            this.subscriptions.add(
                this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
                    this.dispose();
                })
            );
        }
    }

    public ngOnDestroy(): void {
        this.darkmodeService.unsubscribe(this.themeHandler);
        this.subscriptions.unsubscribe();
    }

    public afterClosed(): Observable<ModalClosedResult> {
        return this.subject;
    }

    /**
     * closed reason modal.
     * @returns boolean
     */
    public cancel(): boolean {
        return this.close(ModalClosedReason.CANCELLED);
    }

    /**
     * closed reason modal
     * @returns boolean
     */
    protected back(): boolean {
        return this.close(ModalClosedReason.BACK);
    }

    /**
     *
     * @param reason
     * @param value
     * @returns boolean
     */
    public close(reason: ModalClosedReason, value?: any): boolean {
        if (reason === ModalClosedReason.CANCELLED && !this.modalOptions.cancelable) {
            return false;
        }
        if (reason !== ModalClosedReason.LOADER) {
            modalNumber--;
        }
        this.removeBodyClass();
        this.subject.next(new ModalClosedResult(reason, value));
        this.subject.complete();
        return true;
    }

    private injectComponent<ComponentT extends ModalContent<OptionsT>, OptionsT>(
        component: Type<ComponentT>,
        hostElement: ViewContainerRef,
        modalOptions: ModalOptions<OptionsT>
    ) {
        const injectComponent = hostElement.createComponent(component);
        this.modalOptions = modalOptions;
        injectComponent.instance.setArguments((reason: ModalClosedReason, data?: any) => this.close(reason, data), modalOptions);
    }

    /**
     *
     * @param modalOptions
     */
    public setModalFooter<OptionsT>(modalOptions: ModalOptions<OptionsT>): void {
        try {
            if (modalOptions.footerTemplate) {
                this.injectComponent(modalOptions.footerTemplate, this.componentFooter(), modalOptions);
            }
        } catch (e) {
            console.error(`ModalFooter-Error`, e);
            throw e;
        }
    }

    /**
     *
     * @param contentComponentType
     * @param modalOptions
     */
    public setModalContent<ComponentT extends ModalContent<OptionsT>, OptionsT>(
        contentComponentType: Type<ComponentT>,
        modalOptions: ModalOptions<OptionsT>
    ): void {
        try {
            this.addBodyClass();
            this.injectComponent(contentComponentType, this.componentContainer(), modalOptions);
        } catch (e) {
            console.error(`Modal-Error`, e);
            throw e;
        }
    }

    protected dispose(): void {
        if (!this.subject.closed) {
            this.subject.complete();
        }
    }

    private addBodyClass(): void {
        this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
    }

    // Allow page to scroll again if all modals are closed
    private removeBodyClass(): void {
        const {
            document: { body }
        } = this;
        requestAnimationFrame(() => {
            const numModals = body.querySelectorAll('ap-modal').length;
            numModals === 0 && this.renderer.removeStyle(body, 'overflow');
        });
    }

    private isMouseEventInModal(event: MouseEvent): boolean {
        const modalElement = this.elementRef.nativeElement.querySelector('.ap-modal__dialog') as HTMLElement;

        if (!modalElement) {
            return false;
        }
        const rect = modalElement.getBoundingClientRect();
        const insideModal =
            rect.top <= event.clientY &&
            event.clientY <= rect.top + rect.height &&
            rect.left <= event.clientX &&
            event.clientX <= rect.left + rect.width;
        const wrapperEl = (event.target as HTMLElement).closest('.ap-modal__wrapper');
        return !!wrapperEl || insideModal;
    }
}
